import React from 'react'

import { SxProps } from '@mui/material'
import { Context } from 'types/GlobalUserEvents'
import { TGroup } from 'types/GlobalGroup'

import { useCustomer } from 'redux/hooks/Customer'
import { useDashboardGroupsInGroup } from 'redux/hooks/DashboardGroups'
import { useGroups } from 'redux/hooks/Groups'
import { useResources } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import ContentBox from 'components_new/molecules/ContentBox'
import Emoji from 'components_new/atoms/Emoji'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import GroupAvatar from 'components_new/molecules/GroupAvatar'
import SectionHeader from 'components_new/molecules/SectionHeader'

import KeyMetricsTree from 'components_new/organisms/KeyMetricsTree'
import ResourcesTable from 'components_new/organisms/ResourcesTable'

import Loading from './loading'

interface GroupPreviewItemProps {
  group: TGroup
}

const GroupPreviewItem = (props: GroupPreviewItemProps) => {
  const { group } = props

  const { t } = useTranslation()
  const customer = useCustomer()
  const dashboardGroups = useDashboardGroupsInGroup(group.id)
  const resources = useResources({
    dashboardGroups,
    reports: [] // $TODO
  })

  const isEmpty = dashboardGroups.length === 0 && group.key_metrics.length === 0

  return (
    <ContentBox
      border={true}
      header={{
        icon: <GroupAvatar color={group.profile_color} title={group.name} />,
        size: 'large',
        title: group.name,
        to: `/groups/${group.id}`
      }}
      sx={{ mb: 8 }}
    >
      {isEmpty ? (
        <EmptyPlaceholder
          actions={
            <Button to={`/groups/${group.id}`}>
              {t('groups', 'to_group')}
            </Button>
          }
          background={true}
          body={t('groups', 'no_content_body')}
          height={200}
          size="small"
          title={
            <>
              {t('groups', 'no_content_title')} <Emoji name="seedling" />
            </>
          }
        />
      ) : (
        <>
          {customer?.allow_access.allow_key_metrics &&
          group.key_metrics.length > 0 ? (
                <KeyMetricsTree
                  editMode={false}
                  keyMetrics={group.key_metrics}
                  preview={true}
                />
              ) : null}

          {dashboardGroups.length > 0 ? (
            <>
              <SectionHeader
                color="secondary"
                divider={true}
                gutterBottom={true}
                icon={{ color: 'disabled', name: 'Resources' }}
                size="small"
                title={t('global', 'resources')}
                sx={{ mt: 2 }}
              />

              <ResourcesTable
                context={Context.FROM_HOME_GROUP_PREVIEW}
                items={resources}
                preview={true}
                viewAllHref={`/groups/${group.id}/resources`}
              />
            </>
          ) : null}
        </>
      )}
    </ContentBox>
  )
}

interface HomeGroupPreviewsProps {
  sx?: SxProps
}

const HomeGroupPreviews = (props: HomeGroupPreviewsProps) => {
  const { sx } = props

  const { myGroups, loading } = useGroups()

  if (loading) {
    return <Loading />
  }

  return (
    <Box sx={sx}>
      {myGroups
        .filter((group) => group.show_on_home)
        .map((group) => (
          <GroupPreviewItem group={group} key={group.id} />
        ))}
    </Box>
  )
}

export default HomeGroupPreviews
